import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Grid, Card, CardContent, Typography, TextField, Box, Avatar, Pagination } from '@mui/material';
import { useParams } from 'react-router-dom';
import '../../../../assets/styles/Bidding/PlayersTab.css'
import { BACKEND_DOMAIN } from '../../../../common/env';

const SoldPlayersTab = () => {
  const [soldPlayers, setSoldPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [playersPerPage] = useState(8);
  const { auctionId } = useParams();

  useEffect(() => {
    const fetchSoldPlayers = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/players`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Filter the sold players
        const filteredPlayers = response.data.players.filter((player) => player.isSold === true);
        setSoldPlayers(filteredPlayers);
      } catch (error) {
        console.error("Error fetching sold players:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSoldPlayers();
  }, [auctionId]);

  // Filter players based on search term
  const filteredPlayers = soldPlayers.filter(
    (player) =>
      player.playerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.playerType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const indexOfLastPlayer = page * playersPerPage;
  const indexOfFirstPlayer = indexOfLastPlayer - playersPerPage;
  const currentPlayers = filteredPlayers.slice(indexOfFirstPlayer, indexOfLastPlayer);
  const totalPages = Math.ceil(filteredPlayers.length / playersPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  if (loading) return <Typography>Loading sold players...</Typography>;

  return (
    <Box
      className="players-tab-container"
      sx={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        margin: 0
      }}
    >
      {/* Search Bar - Centered with top margin */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        width: '100%',
        maxWidth: "1200px",
        margin: "16px auto 10px", // Added top margin
        position: "relative",
        top: 0,
        padding: { xs: '0 16px', sm: 0 }
      }}>
        <TextField
          label="Search by ID or Name"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ 
            width: { xs: "200px", sm: "200px" },
            maxWidth: { xs: "200px", sm: "200px" },
            '& .MuiInputBase-root': {
              height: '40px', // Make the input smaller
              alignItems: 'center'
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px' // More rounded corners
            },
            '& .MuiInputLabel-root': {
              transform: 'translate(14px, 10px) scale(1)', // Better label alignment
              fontSize: '0.9rem'
            },
            '& .MuiInputLabel-shrink': {
              transform: 'translate(14px, -6px) scale(0.75)' // Proper shrink alignment
            }
          }}
        />
      </Box>

      {/* Players Grid */}
      {currentPlayers.length > 0 ? (
        <>
          <Grid container spacing={2} className="players-grid" sx={{ 
            maxWidth: "1200px", 
            position: "relative", 
            top: 0,
            width: "100%",
            margin: "0 auto",
            marginTop: "10px",
            padding: { xs: 1, sm: 0 }
          }}>
            {currentPlayers.map((player) => (
              <Grid item xs={12} sm={6} md={4} key={player._id}>
                <Card className="player-card-compact">
                  <Box className="player-content-wrapper" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', height: '100%' }}>
                    {/* Player Logo Section - Left */}
                    <Box className="player-logo-section" sx={{ 
                      width: '30%', 
                      minWidth: '80px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      padding: '12px',
                      borderRight: '1px solid #e2e8f0'
                    }}>
                      {player.playerLogo ? (
                        <Box
                          component="img"
                          src={`${player.playerLogo}`}
                          alt={player.playerName}
                          sx={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            borderRadius: '8px'
                          }}
                        />
                      ) : (
                        <Box
                          sx={{ 
                            width: '100%', 
                            height: '100%', 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '8px',
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: '#666'
                          }}
                        >
                          {player.playerName.charAt(0).toUpperCase()}
                        </Box>
                      )}
                    </Box>

                    {/* Player Details Section - Right */}
                    <Box className="player-details-section" sx={{ width: '70%', padding: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <CardContent className="player-details-compact" sx={{ padding: "0", flex: '1' }}>
                        <Typography className="player-name-compact" sx={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "4px" }}>
                          {player.playerName}
                        </Typography>
                        <Typography className="player-info-compact" sx={{ fontSize: "0.8rem", color: "#666", marginBottom: "2px" }}>
                          <strong>ID:</strong> {player.customID}
                        </Typography>
                        <Typography className="player-info-compact" sx={{ fontSize: "0.8rem", color: "#666", marginBottom: "2px" }}>
                          <strong>Type:</strong> {player.playerType}
                        </Typography>
                        <Typography className="player-info-compact" sx={{ fontSize: "0.8rem", color: "#666", marginBottom: "8px" }}>
                          <strong>Base:</strong> ${player.baseValue}
                        </Typography>
                      </CardContent>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, mt: 2, mb: 2 }}>
                      {/* Prev */}
                      {page > 1 && (
                        <Typography 
                          onClick={() => handlePageChange(null, page - 1)}
                          sx={{
                            cursor: 'pointer',
                            fontSize: '1.075rem',
                            fontWeight: 500,
                            color: '#3f51b5',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: '#303f9f',
                            }
                          }}
                        >
                          &lt; Prev
                        </Typography>
                      )}
          
                      {/* Visible pages: current ± 1 */}
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p >= page - 1 && p <= page + 1)
                        .map(p => (
                          <Typography
                            key={p}
                            onClick={() => handlePageChange(null, p)}
                            sx={{
                              cursor: 'pointer',
                              fontSize: '1.075rem',
                              fontWeight: p === page ? 600 : 400,
                              color: p === page ? '#3f51b5' : '#666',
                              textDecoration: p === page ? 'underline' : 'none',
                              '&:hover': {
                                color: '#303f9f',
                                textDecoration: 'underline',
                              }
                            }}
                          >
                            {p}
                          </Typography>
                        ))}
          
                      {/* Next */}
                      {page < totalPages && (
                        <Typography 
                          onClick={() => handlePageChange(null, page + 1)}
                          sx={{
                            cursor: 'pointer',
                            fontSize: '1.075rem',
                            fontWeight: 500,
                            color: '#3f51b5',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: '#303f9f',
                            }
                          }}
                        >
                          Next &gt;
                        </Typography>
                      )}
                    </Box>
        </>
      ) : (
        <Typography variant="h6" sx={{ marginTop: 4, position: "relative", top: "-20px" }}>
          No players match your search.
        </Typography>
      )}
    </Box>
  );
};

export default SoldPlayersTab;